import { useState, useEffect } from 'react';
import { Bell, CheckCircle, AlertTriangle, Info, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { ScrollArea } from '@/components/ui/scroll-area';
import { MobileDrawer, MobileDrawerTrigger, MobileDrawerContent, MobileDrawerHeader, MobileDrawerTitle } from '@/components/ui/mobile-drawer';
import { cn } from '@/lib/utils';
import { formatDistanceToNow } from 'date-fns';
import { sanitizeText } from '@/lib/sanitize';

interface Notification {
  id: string;
  title: string;
  message: string;
  type: string;
  read: boolean;
  created_at: string;
}

export function NotificationDrawer() {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return;

    const fetchNotifications = async () => {
      const { data } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(20);

      if (data) {
        setNotifications(data);
        setUnreadCount(data.filter(n => !n.read).length);
      }
    };

    fetchNotifications();

    const channel = supabase
      .channel('notifications')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`
        },
        () => fetchNotifications()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const markAsRead = async (id: string) => {
    await supabase
      .from('notifications')
      .update({ read: true })
      .eq('id', id);
  };

  const markAllAsRead = async () => {
    if (!user) return;
    await supabase
      .from('notifications')
      .update({ read: true })
      .eq('user_id', user.id)
      .eq('read', false);
  };

  const deleteNotification = async (id: string) => {
    await supabase.from('notifications').delete().eq('id', id);
  };

  const getIcon = (type: string) => {
    switch (type) {
      case 'success':
        return <CheckCircle className="text-success" size={20} />;
      case 'warning':
        return <AlertTriangle className="text-warning" size={20} />;
      case 'error':
        return <AlertTriangle className="text-destructive" size={20} />;
      default:
        return <Info className="text-primary" size={20} />;
    }
  };

  return (
    <MobileDrawer open={open} onOpenChange={setOpen}>
      <MobileDrawerTrigger asChild>
        <button className="relative p-2.5 hover:bg-surface rounded-2xl transition-colors">
          <Bell size={20} className="text-muted-foreground" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 bg-destructive text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
      </MobileDrawerTrigger>
      <MobileDrawerContent className="max-h-[85vh]">
        <MobileDrawerHeader className="flex-row items-center justify-between">
          <MobileDrawerTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5" />
            Notifications
            {unreadCount > 0 && (
              <span className="bg-primary/10 text-primary text-xs font-semibold px-2 py-0.5 rounded-full">
                {unreadCount} new
              </span>
            )}
          </MobileDrawerTitle>
          {unreadCount > 0 && (
            <Button variant="ghost" size="sm" onClick={markAllAsRead} className="text-xs rounded-xl">
              Mark all read
            </Button>
          )}
        </MobileDrawerHeader>

        <ScrollArea className="h-[60vh] px-4">
          {notifications.length === 0 ? (
            <div className="py-16 text-center">
              <div className="w-16 h-16 rounded-3xl bg-surface flex items-center justify-center mx-auto mb-4">
                <Bell size={32} className="text-muted-foreground/30" />
              </div>
              <p className="text-muted-foreground font-medium">No notifications yet</p>
              <p className="text-sm text-muted-foreground/70 mt-1">
                We'll notify you when something important happens
              </p>
            </div>
          ) : (
            <div className="space-y-2 pb-4">
              {notifications.map(notification => (
                <div
                  key={notification.id}
                  className={cn(
                    "p-4 rounded-2xl transition-colors",
                    !notification.read ? 'bg-primary/5 border border-primary/10' : 'bg-surface'
                  )}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-background flex items-center justify-center flex-shrink-0">
                      {getIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-foreground text-sm line-clamp-1">
                        {sanitizeText(notification.title)}
                      </h4>
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                        {sanitizeText(notification.message)}
                      </p>
                      <p className="text-xs text-muted-foreground/70 mt-2">
                        {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                      </p>
                    </div>
                    <div className="flex flex-col gap-1">
                      {!notification.read && (
                        <button
                          onClick={() => markAsRead(notification.id)}
                          className="text-primary hover:text-primary/80 text-xs font-medium"
                        >
                          Read
                        </button>
                      )}
                      <button
                        onClick={() => deleteNotification(notification.id)}
                        className="text-muted-foreground hover:text-destructive transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </MobileDrawerContent>
    </MobileDrawer>
  );
}
